import { useMemo } from 'react';
import { DropdownItemProps } from 'semantic-ui-react';
import orderBy from 'lodash/orderBy';
import deburr from 'lodash/deburr';
import { Location } from '../types';
import { LocationsApi } from '../api';
import { ApiError, defaultErrorHandler } from 'api';
import { useQuery } from 'react-query';

export interface LocationLists {
  locations: Location[];
  dropdownItems: DropdownItemProps[];
  indexedLocations: Record<number, Location>;
  isFetched: boolean;
  isFetching: boolean;
}

export const useFetchLocations = (options?: {
  staleTime?: number;
}): LocationLists => {
  const {
    data: locations,
    isFetching,
    isFetched,
  } = useQuery('locations', LocationsApi.getAll, {
    staleTime: 15 * 60 * 1000,
    onSettled: (_, error) =>
      error && error instanceof ApiError && defaultErrorHandler(error),
    ...options,
  });

  const indexedLocations = useMemo(
    () =>
      (locations || []).reduce((result, item) => {
        result[item.id] = item;
        return result;
      }, {} as Record<number, Location>),
    [locations]
  );

  const dropdownItems = useMemo(() => {
    const activeLocations = (locations || []).filter(
      (location) => location.isActive
    );

    const items = activeLocations.map((location) => ({
      key: location.id,
      value: location.id,
      text: location.name,
    }));

    const sortedItems = orderBy(items, (item) =>
      deburr(item.text).toLowerCase()
    );

    return sortedItems;
  }, [locations]);

  return {
    locations: locations || [],
    dropdownItems,
    indexedLocations,
    isFetched,
    isFetching,
  };
};
